/**
 * @file Abstract class for input with a lookup functionality
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { OnInit, EventEmitter } from '@angular/core';
import { DropdownInput } from '../../../base/inputs/input-dropdown/input-dropdown.component';
import { DataType } from 'breeze-client';
import { FilterOperators } from '../../../core/helpers/filter-operators';
import { PropertyFilter } from '../../../core/helpers/property-filter';
import { SortDescriptor } from '../../../core/helpers/sort-descriptor';
import { keyCodes } from '../../../base/helpers/key-codes';
import { SortDirection } from '../../../core/helpers/sort-direction';
import { DataSet } from '../../../core/helpers/data-set';
import * as moment from 'moment';
/**
 * Class for the lookup input component
 */
var LookupInput = /** @class */ (function (_super) {
    tslib_1.__extends(LookupInput, _super);
    /**
     * Constructs a LookupInput
     */
    function LookupInput(breezeService, alertsService, translateService, localisationService) {
        var _this = _super.call(this) || this;
        _this.breezeService = breezeService;
        _this.alertsService = alertsService;
        _this.translateService = translateService;
        _this.localisationService = localisationService;
        /** Event triggered when a new data set is retrieved from the backend api.
         * @type {EventEmitter<QueryResult>}
         * @memberOf LookupInput
         */
        _this.onRefresh = new EventEmitter();
        /**
         * Triggered when the dropdown opens (true) or closes (false).
         * @type {EventEmitter<boolean>}
         * @memberOf LookupInput
         */
        _this.onDropdownChanged = new EventEmitter();
        // Use to hide the loading button
        _this.showButton = true;
        /**
         * Gets, sets the place holder text of the lookup.
         * @see setPlaceHolderText method.
         */
        _this.placeHolderText = '';
        /**
         * Holds the reference of the entity format.
         * @private
         * @type {EntityFormat}
         */
        _this.formatValue = undefined;
        /**
         * Holds the last search criteria.
         * @private
         * @type {string}
         */
        _this.lastSearchCriteria = '';
        /**
         * The items found with matching score.
         * @private
         * @type {{item: Entity, match: number}[]}
         */
        _this.foundItems = [];
        /** Keep track of loading state */
        _this.loading = false;
        /** Show not found message */
        _this.notFound = false;
        /** If in editmode do not update textValue */
        _this.inEditMode = false;
        _this.timeoutId = 0;
        _this.showItems = 1000;
        /** Total found items (in interface are only five visible) */
        _this.inlineCount = 0;
        /** The found items */
        _this.items = [];
        /** Sorting rules used to create the EntityQuery. */
        _this.sorting = [];
        /** The source name of the requested entities. */
        _this.sourceName = undefined;
        /** The item the dropdown is focussed on */
        _this.focussedValue = null;
        return _this;
    }
    Object.defineProperty(LookupInput.prototype, "placeholder", {
        /**
         * Sets the placeholder text
         * @memberOf LookupInput
         */
        set: function (value) {
            this.placeHolderText = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LookupInput.prototype, "format", {
        /**
         * Sets the entity format of the lookup, which entity and properties are used.
         */
        get: function () {
            return this.formatValue;
        },
        /**
         * Sets the entity format of the lookup, which entity and properties are used.
         */
        set: function (value) {
            var _this = this;
            if (value === this.formatValue) {
                return;
            }
            this.formatValue = value;
            this.sourceName = this.formatValue.dataSource;
            if (this.formatValue.displayFormat) {
                // Sort fields in display order to get the appropriate dataSet sorting order.
                this.formatValue.fields.sort(function (a, b) {
                    return _this.formatValue.displayFormat.indexOf('[' + a + ']') - _this.formatValue.displayFormat.indexOf('[' + b + ']');
                });
            }
            this.breezeService.registerSource(this.formatValue.entity, this);
            if (this.formatValue.fields) {
                // Clear the sorting and add sort descriptor
                this.sorting.length = 0;
                this.formatValue.fields.forEach(function (field) {
                    _this.sorting.push(new SortDescriptor(field, field, SortDirection.Descending, 0));
                });
            }
            // Not just for dropdown. Also forces to listen. This will set this.entityType.
            if (this.formatValue.dropdown) {
                this.lookup(null);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LookupInput.prototype, "queryOptions", {
        /** Aditional options to generate the requested EntityQuery */
        get: function () {
            var result = {
                skip: 0
            };
            if (!this.formatValue.dropdown) {
                result['take'] = this.showItems;
            }
            if (this.formatValue.parameters) {
                result['parameters'] = this.formatValue.parameters;
            }
            return result;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LookupInput.prototype, "oid", {
        /** Gets the oid from the selected entity. */
        get: function () {
            var entity = this.value;
            if (entity) {
                return entity.entityAspect.getKey().values[0];
            }
            return '';
        },
        /** Tries to sets the oid by finding the entity.  */
        set: function (newValue) {
            if (this.oid === newValue) {
                return;
            }
            var nameKey = this.entityType.keyProperties[0].nameOnServer;
            this.filter = new PropertyFilter(this, nameKey, FilterOperators.Equals, newValue);
            this.afterSetOid();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the string representation of the lookup value.
     */
    LookupInput.prototype.getDisplayValue = function () {
        return this.getDisplayValueForItem(this.value);
    };
    /**
     * Gets text representation of selected entity.
     * @param {*} item The selected entity.
     * @returns The representation text.
     */
    LookupInput.prototype.getDisplayValueForItem = function (item) {
        var _this = this;
        // No item case
        if (item == null) {
            return '';
        }
        // No format given
        if (!this.formatValue || !this.formatValue.fields) {
            return this.translateService.instant('APP.INPUTS.NO_FORMAT');
        }
        // Item and formatValue are given
        var text = (this.formatValue.displayFormat) ? this.formatValue.displayFormat : '';
        if (this.formatValue.displayFormat) {
            this.formatValue.fields.forEach(function (field) {
                var value = item[field];
                if (moment.isDate(value) || moment.isMoment(value)) {
                    value = moment(value).format(_this.localisationService.currentLanguage.formats.datetime.format);
                }
                text = text.replace("[" + field + "]", value ? value : '');
            });
        }
        else {
            this.formatValue.fields.forEach(function (field) {
                var value = item[field];
                if (moment.isDate(value) || moment.isMoment(value)) {
                    value = moment(value).format(_this.localisationService.currentLanguage.formats.datetime.format);
                }
                text += value + ' ';
            });
        }
        return text.trim();
    };
    /**
     * Is called right after the directive's data-bound properties have been checked for the
     * first time, and before any of its children have been checked.It is invoked only once when the
     * directive is instantiated.
     */
    LookupInput.prototype.ngOnInit = function () {
        this.setPlaceHolderText();
        if (!this.formatValue) {
            return;
        }
        if (this.value) {
            // Forcing to set this.textValue now we have this.format.
            this.setValue(this.value);
        }
    };
    /**
     * Is component in loading state
     * @return Returns true if component is loading
     */
    LookupInput.prototype.isLoading = function () {
        return this.loading;
    };
    /**
     * Returns whether the dropdown is open
     * @returns Returns if dropdown is open
     */
    LookupInput.prototype.isOpen = function () {
        return this.items.length > 0 || this.notFound;
    };
    /** Refresh the domain data source */
    LookupInput.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var result, _a, _b;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        result = this.breezeService.getDataSet(this, this.items);
                        _b = (_a = this.onRefresh).emit;
                        return [4 /*yield*/, result];
                    case 1:
                        _b.apply(_a, [_c.sent()]);
                        return [2 /*return*/, result];
                }
            });
        });
    };
    /**
     * User selects an item.
     * @param Entity The selected item.
     */
    LookupInput.prototype.select = function (item) {
        // Not in edit mode anymore.
        this.inEditMode = false;
        // Set the entity field.
        this.value = item;
        // Clean up the items list.
        this.items.length = 0;
        // Notifies that the dropdown closes.
        this.onDropdownChanged.emit(false);
    };
    /**
     * Is called on focus event.
     * To reset the textValue.
     */
    LookupInput.prototype.onFocus = function () {
        if (this.oid === '') {
            this.textValue = '';
        }
        this.lookup(null);
    };
    /**
     * Is called on focusout event.
     * Sets value to null when textValue is empty
     * @memberOf LookupInput
     */
    LookupInput.prototype.onFocusOut = function () {
        // Fix: Leegmaken Entity input heeft soms geen effect #2806
        if (this.textValue === '') {
            this.inEditMode = false;
            this.setValue(null);
        }
    };
    /**
     * Function called when the menu is opened
     */
    LookupInput.prototype.dropDownOpened = function () {
        if (this.focussedValue === null) {
            this.focussedValue = this.value;
        }
        var position = this.items.indexOf(this.focussedValue);
        if (position === -1 && this.items.length > 0) {
            this.focussedValue = this.items[0];
        }
    };
    /**
     * Listener for global key down events
     * @param $event The event
     */
    LookupInput.prototype.keyDown = function (event) {
        /**
         * We gebruiken out-zone vanwege performance issue:
         * Het type in input wordt langzaam wanneer je ook een groot gevulde agenda open hebt.
         * Zover ik kan zien werkt het hier zonder this.changeDetector.detectChanges().
         * Bij problemen implementeer toch this.changeDetector.detectChanges()
         * See https://stackoverflow.com/questions/45227486/hostlistener-slowing-down-application-angular2
         */
        if (this.focussedValue === null) {
            this.focussedValue = this.value;
        }
        // Only proceed if the dropdown is *actually* open
        if (this.isOpen()) {
            var position = this.items.indexOf(this.focussedValue);
            // Stop if current item is not found in items
            if (position === -1) {
                position = 0;
                this.focussedValue = null;
            }
            // Based on key code go up or down
            if (event.keyCode === keyCodes['up']) {
                // Stop if current is at the top of the items
                if (position === 0) {
                    return;
                }
                // Set previous position
                position = position - 1;
            }
            else if (event.keyCode === keyCodes['down']) {
                // Stop if current is at the bottom of the items
                if (position === this.items.length - 1) {
                    return;
                }
                // Set next position
                position = position + 1;
            }
            else if (event.keyCode === keyCodes['enter']) {
                if (this.focussedValue !== null) {
                    // this.value = this.focussedValue;
                    this.setValue(this.focussedValue);
                }
            }
            // Set the new value
            this.focussedValue = this.items[position];
        }
    };
    LookupInput.prototype.isActive = function (item) {
        if (this.focussedValue !== null) {
            return item === this.focussedValue;
        }
        return false;
    };
    /**
     * Used to close the dropdown
     */
    LookupInput.prototype.setClose = function () {
        // Make items list empty (but keep reference)
        this.items.length = 0;
        this.inlineCount = 0;
        // Not in edit mode anymore
        this.inEditMode = false;
    };
    /**
     * Update the text value
     */
    LookupInput.prototype.updateTextValue = function () {
        this.textValue = this.getDisplayValue();
    };
    /**
     * Set the value of the input and the text displayed.
     * @param val The new val to set.
     */
    LookupInput.prototype.setValue = function (val) {
        // Set the value
        _super.prototype.setValue.call(this, val);
        // If not in edit mode update the text value
        if (!this.inEditMode) {
            this.updateTextValue();
        }
    };
    /**
     * User hits enter in input element; update input field
     * @param Entity The selected item
     */
    LookupInput.prototype.leaveViaEnter = function (event) {
        if (this.focussedValue !== null) {
            this.setValue(this.focussedValue);
        }
        // Update the value in input element
        this.updateTextValue();
        // Close dropdown etc
        this.setClose();
        // Stop the enter event from bubbling
        event.preventDefault();
    };
    /**
     * An outside click event
     */
    LookupInput.prototype.clickOutside = function (e) {
        this.updateTextValue();
        if (this.isOpen()) {
            this.setClose();
        }
    };
    /**
     * Handles the user input per character
     */
    LookupInput.prototype.lookup = function (event) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                // IMPROVE: use key codes (key-codes.ts)
                if (event) {
                    switch (event.keyCode) {
                        case 38: // Ignore Arrow up see HostListener document:keydown
                        case 40: // Ignore Arrow down see HostListener document:keydown
                        case 13: // Ignore enter key?
                            return [2 /*return*/];
                        default:
                            break;
                    }
                }
                // Set in edit mode
                this.inEditMode = true;
                // Optimistic approach :-)
                this.notFound = false;
                // Set in loading state only when we go to search.
                this.loading = this.formatValue.dropdown || (!(this.lastSearchCriteria === this.textValue && this.items.length > 0) && !((this.textValue + '').trim() === ''));
                // this.loading = this.formatValue.dropdown || (!(this.lastSearchCriteria === this.textValue && this.items.length > 0)); // && !((this.textValue + '').trim() === ''));
                clearTimeout(this.timeoutId);
                this.timeoutId = window.setTimeout(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                    var dataset, index;
                    var _this = this;
                    return tslib_1.__generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                // Clear filter.
                                this.filter = null;
                                if (!this.formatValue.dropdown) {
                                    if (this.lastSearchCriteria === this.textValue && this.items.length > 0) {
                                        this.loading = false;
                                        this.onDropdownChanged.emit(false);
                                        return [2 /*return*/];
                                    }
                                    this.inlineCount = 0;
                                    if ((this.textValue + '').trim() === '' && event !== null) {
                                        // this.items.splice(0, this.items.length);
                                        this.items.length = 0;
                                        this.loading = false;
                                        this.onDropdownChanged.emit(false);
                                        return [2 /*return*/];
                                    }
                                    this.lastSearchCriteria = this.textValue;
                                    this.loading = true;
                                    this.onDropdownChanged.emit(true);
                                    this.incrementalSearch(this.textValue, event === null, function () {
                                        var index = _this.items.length;
                                        var _loop_1 = function () {
                                            index--;
                                            var item = _this.items[index];
                                            if (_this.foundItems.findIndex(function (it) { return item === it.item; }) === -1) {
                                                _this.items.splice(index, 1);
                                            }
                                        };
                                        while (index > 0) {
                                            _loop_1();
                                        }
                                        _this.clientSideMatch();
                                        _this.items.sort(function (itemA, itemB) {
                                            var foundA = _this.foundItems.find(function (it) { return it.item === itemA; });
                                            var foundB = _this.foundItems.find(function (it) { return it.item === itemB; });
                                            var matchA = foundA == null ? 0 : foundA.match;
                                            var matchB = foundB == null ? 0 : foundB.match;
                                            return matchB - matchA;
                                        });
                                        if (_this.items.length !== 0) {
                                            var found = _this.items.indexOf(_this.value);
                                            if (found === -1) {
                                                _this.focussedValue = _this.items[0];
                                            }
                                            else {
                                                _this.focussedValue = _this.items[found];
                                            }
                                        }
                                        // Stop loading state
                                        _this.loading = false;
                                        _this.onDropdownChanged.emit(false);
                                    });
                                    return [2 /*return*/];
                                }
                                return [4 /*yield*/, this.refresh()];
                            case 1:
                                dataset = _a.sent();
                                if (this.items.length !== 0) {
                                    index = this.items.indexOf(this.value);
                                    if (index === -1) {
                                        this.focussedValue = this.items[0];
                                    }
                                    else {
                                        this.focussedValue = this.items[index];
                                    }
                                }
                                // Stop loading state
                                this.loading = false;
                                this.onDropdownChanged.emit(true);
                                // Store inline count
                                this.inlineCount = dataset.inlineCount;
                                return [2 /*return*/];
                        }
                    });
                }); }, 500);
                return [2 /*return*/];
            });
        });
    };
    /**
     * Sets the place holder to the default text for lookup inputs.
     * Override this method when you want a more specific place holder text.
     * @protected
     */
    LookupInput.prototype.setPlaceHolderText = function () {
        var _this = this;
        if (this.placeHolderText !== '') {
            // It was set in the code.
            return;
        }
        if (this.entityType) {
            var entityPlaceHolder_1 = 'APP.MISC.' + this.entityType.shortName.toUpperCase() + '.#SEARCH';
            this.subscriptionPool.add(this.translateService.get(['APP.INPUTS.SEARCH_BY_TYPING', entityPlaceHolder_1]).subscribe(function (result) {
                if (result[entityPlaceHolder_1] === entityPlaceHolder_1) {
                    _this.placeHolderText = result['APP.INPUTS.SEARCH_BY_TYPING'];
                    return;
                }
                _this.placeHolderText = result[entityPlaceHolder_1];
            }));
            return;
        }
        this.subscriptionPool.add(this.translateService.get('APP.INPUTS.SEARCH_BY_TYPING').subscribe(function (result) {
            _this.placeHolderText = result;
        }));
    };
    /**
     * Search incrementally to approximate the feeling of a Google like search.
     * @protected
     * @param {string} searchText The text to match.
     * @param {Function} callback Callback with the next stap in the search.
     */
    LookupInput.prototype.incrementalSearch = function (searchText, onFocus, callback) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var match, dataSet_1, result_1, dataSet, splitAt, queryResult, left, right, result, lastSearch;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        match = (searchText + '').trim();
                        if (!onFocus) return [3 /*break*/, 2];
                        dataSet_1 = this.makeDataSet();
                        return [4 /*yield*/, dataSet_1.refresh()];
                    case 1:
                        result_1 = _a.sent();
                        this.inlineCount = result_1.inlineCount;
                        result_1.results.forEach(function (item) {
                            var found = _this.items.indexOf(item);
                            if (found === -1) {
                                _this.items.splice(0, 0, item);
                            }
                            else {
                                _this.items.splice(found, 1);
                                _this.items.splice(0, 0, item);
                            }
                            _this.foundItems.push({ item: item, match: match.length });
                        });
                        callback();
                        return [2 /*return*/];
                    case 2:
                        if (!(match === '')) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.refresh()];
                    case 3:
                        _a.sent();
                        callback();
                        return [2 /*return*/];
                    case 4:
                        dataSet = this.makeDataSet();
                        this.formatValue.fields.forEach(function (field) {
                            // Match the full searchText
                            // Hier wordt op alle getoonde "String" velden gezocht.
                            // https://github.com/Symagic/html5/issues/3946
                            // https://github.com/Symagic/html5/issues/4046
                            var property = _this.entityType.getDataProperty(field);
                            if (property.dataType === DataType.String) {
                                // Aanmaak van een OR filter van alle getoonde "String" velden.
                                // Ik gebruik hier een dummy anders klaagt ts-lint.
                                var dummy = new PropertyFilter(dataSet, field, FilterOperators.Contains, match, FilterOperators.Or);
                            }
                        });
                        splitAt = match.lastIndexOf(' ');
                        if (!(splitAt === -1)) return [3 /*break*/, 6];
                        return [4 /*yield*/, dataSet.refresh()];
                    case 5:
                        queryResult = _a.sent();
                        if ((this.lastSearchCriteria + '').trim() === match) {
                            this.inlineCount = queryResult.inlineCount;
                            // this.foundItems.splice(0, this.foundItems.length);
                            this.foundItems.length = 0;
                            if (queryResult.results.length === 0) {
                                // this.items.splice(0, this.items.length);
                                this.items.length = 0;
                            }
                            else {
                                queryResult.results.forEach(function (item) {
                                    var found = _this.items.indexOf(item);
                                    if (found === -1) {
                                        _this.items.splice(0, 0, item);
                                    }
                                    else {
                                        _this.items.splice(found, 1);
                                        _this.items.splice(0, 0, item);
                                    }
                                    _this.foundItems.push({ item: item, match: match.length });
                                });
                            }
                        }
                        else {
                            queryResult.results.forEach(function (item) {
                                if (_this.items.find(function (it) { return it === item; }) == null) {
                                    _this.items.push(item);
                                }
                                var found = _this.foundItems.find(function (it) { return item === it.item; });
                                if (found == null) {
                                    _this.foundItems.push({ item: item, match: match.length });
                                }
                                else {
                                    found.match += match.length;
                                }
                            });
                        }
                        callback();
                        return [2 /*return*/];
                    case 6:
                        left = match.substring(0, splitAt).trim();
                        right = match.substring(splitAt + 1).trim();
                        return [4 /*yield*/, dataSet.refresh()];
                    case 7:
                        result = _a.sent();
                        lastSearch = (this.lastSearchCriteria + '').trim();
                        if (lastSearch === match) {
                            this.inlineCount = result.inlineCount;
                            // this.foundItems.splice(0, this.foundItems.length);
                            this.foundItems.length = 0;
                            result.results.forEach(function (item) {
                                var index = _this.items.indexOf(item);
                                if (index === -1) {
                                    _this.items.splice(0, 0, item);
                                }
                                else {
                                    _this.items.splice(index, 1);
                                    _this.items.splice(0, 0, item);
                                }
                                _this.foundItems.push({ item: item, match: match.length });
                            });
                            if (result.results.length > 0) {
                                // Found items with total search criteria.
                                // We do not to have search sub criteria.
                                callback();
                                return [2 /*return*/];
                            }
                        }
                        else {
                            result.results.forEach(function (item) {
                                if (_this.items.find(function (it) { return it === item; }) == null) {
                                    _this.items.push(item);
                                }
                                var found = _this.foundItems.find(function (it) { return item === it.item; });
                                if (found == null) {
                                    _this.foundItems.push({ item: item, match: match.length });
                                }
                                else {
                                    found.match += match.length;
                                }
                            });
                        }
                        if (left.length > right.length) {
                            this.incrementalSearch(left, false, function () {
                                _this.incrementalSearch(right, false, function () {
                                    callback();
                                });
                            });
                            return [2 /*return*/];
                        }
                        this.incrementalSearch(right, false, function () {
                            _this.incrementalSearch(left, false, function () {
                                callback();
                            });
                        });
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Makes a data set for incremantal searching.
     * @protected
     * @returns {DataSet} The data set
     */
    LookupInput.prototype.makeDataSet = function () {
        var dataSet = new DataSet(this.breezeService);
        dataSet.entityType = this.entityType;
        dataSet.queryOptions = this.queryOptions;
        dataSet.sourceName = this.sourceName;
        if (this.formatValue.fields) {
            // Clear the sorting and add sort descriptor
            dataSet.sorting.length = 0;
            this.formatValue.fields.forEach(function (field) {
                dataSet.sorting.push(new SortDescriptor(field, field, SortDirection.Descending, 0));
            });
        }
        return dataSet;
    };
    /**
     * Refreshes the input
     * @private
     * @memberOf LookupInput
     */
    LookupInput.prototype.afterSetOid = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var dataset;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.refresh()];
                    case 1:
                        dataset = _a.sent();
                        this.loading = false;
                        if (dataset.results.length === 1) {
                            this.setValue(dataset.results[0]);
                            this.setClose();
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Adds a client side calculation of the search match
     * to compensate underestimations of the match in the incremental search.
     * @private
     */
    LookupInput.prototype.clientSideMatch = function () {
        var _this = this;
        var criteria = (this.textValue + '').toLowerCase().split(' ');
        if (criteria.length === 1 && criteria[0] === '') {
            return;
        }
        this.foundItems.forEach(function (it) {
            _this.formatValue.fields.forEach(function (field) {
                var value = (it.item[field] + '').toLowerCase();
                if (value === '') {
                    return;
                }
                criteria.forEach(function (match) {
                    if (value.indexOf(match) !== -1) {
                        it.match += match.length;
                    }
                });
            });
        });
    };
    return LookupInput;
}(DropdownInput));
export { LookupInput };
